import React, { useState, useEffect, useLayoutEffect, memo, Suspense } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { CaretDown, CaretRight, Copy, Check, MapPin } from '@phosphor-icons/react';
import { trackCompanyLink } from '../../../../../utils/analytics/analytics';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { CompanyName } from '../../../../../components/CompanyName';
import { SocialMediaButtons } from './SocialMediaButtons';
import { MoneyGraph, MonthlyHiringChart, StarburstChart } from './CompanyCharts';
import { RoleRoleLevelChart } from './RoleRoleLevelChart';
import { DonutSunburstChart } from './DonutSunburstChart';
// CompanyLocationMap is lazy-loaded because it pulls in the entire mapbox-gl
// dependency (~700kB JS + 5kB CSS asset). Statically importing it here would
// merge mapbox into shared chunks and leak the mapbox CSS preload onto pages
// that never render a map (e.g. the landing page). The user must click
// "See Location" to mount the map, so lazy-loading is fully safe.
const CompanyLocationMap = React.lazy(() =>
  import('../../../../../components/CompanyLocationMap').then((m) => ({ default: m.CompanyLocationMap })),
);
import { CompanyInsightCard } from './CompanyInsightCard';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { filterCompanyAboutForPageLocale } from '../../../../../utils/text/is-likely-finnish-prose';
import { resolveRegistryMainBusinessLineForPageLocale } from '../../../../../utils/company/resolve-registry-main-business-line-for-page-locale';
import { getFirstCompanyUrl, extractYouTubeVideoId } from '../utils/companyUtils';
import { isValidNumber, hasAnyValidNumber, getValidNumberOrNull } from '../utils/numberUtils';
import type { DisplayJob } from '../../../../../types';
import {
  readCompanyInfoCollapsedPreference,
  writeCompanyInfoCollapsedPreference,
} from '../utils/job-detail-ui-preferences';
import { buildHiringSnapshotSeoRows } from '../utils/build-hiring-snapshot-seo-rows';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

export type CompanyInfoTabId = 'financial' | 'hiring' | 'roles' | 'locations' | 'languages' | 'map';

export interface CompanySectionProps {
  displayJob: DisplayJob;
  companyId?: string | null;
  companyIndustry?: string | null;
  /** Industry as stated or implied in the job listing (processor). May differ from registry {@link companyIndustry}. */
  listingCompanyIndustry?: string | null;
  companyAbout?: string | null;
  companyContactDetails?: string | null;
  companyRevenue?: number | null;
  companyProfit?: number | null;
  companyProfitMargin?: number | null;
  companyMonthlyCounts?: Record<string, number> | null;
  companyHeadCountMin?: number | null;
  companyHeadCountMax?: number | null;
  companyHeadcountRangeMin?: number | null;
  companyHeadcountRangeMax?: number | null;
  companyHQInFinland?: string | null;
  companyVideo?: string | null;
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  tiktokUrl?: string | null;
  registryMainBusinessLineDescriptionEn?: string | null;
  registryMainBusinessLineDescriptionFi?: string | null;
  registryMainBusinessLineDescriptionSv?: string | null;
  registryCompanyFormDescription?: string | null;
  registryWebsite?: string | null;
  registryMainName?: string | null;
  registryStreet?: string | null;
  registryCity?: string | null;
  registryPostCode?: string | null;
  registryBuildingNumber?: string | null;
  address?: string | null;
  address_fi?: string | null;
  address_sv?: string | null;
  businessID?: string | null;
  registryBusinessId?: string | null;
  companyGeocoordinates?: [number, number] | null;
  currentLocaleTrends?: any;
  companyHiringTrendsByLocale?: {
    en?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
    fi?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
    sv?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
  } | null;
  jobLocale: string;
  brandColour?: string;
  useBrandColor?: boolean;
  hasAnyCharts: boolean;
  chartsCollapsed: boolean;
  onToggleChartsCollapse: () => void;
  companyRef: React.RefObject<HTMLDivElement>;
  isTyomarkkinatori?: boolean;
}

/** Public theme default when company has no brand colour (forest green). */
const DEFAULT_BRAND_GREEN = '#3B5E45';

function hexToRgba(hex: string, alpha: number): string | null {
  const normalized = hex.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(normalized);
  const full = /^#([0-9a-fA-F]{6})$/.exec(normalized);
  if (!short && !full) return null;
  const hexValue = short
    ? short[1]
        .split('')
        .map((ch) => ch + ch)
        .join('')
    : full![1];
  const r = parseInt(hexValue.slice(0, 2), 16);
  const g = parseInt(hexValue.slice(2, 4), 16);
  const b = parseInt(hexValue.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function withHttpsUrl(url: string): string {
  const t = url.trim();
  if (!t) return t;
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

function siteComparableKey(url: string): string | null {
  try {
    const u = new URL(withHttpsUrl(url));
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    const path = u.pathname.replace(/\/$/, '') || '';
    return `${host}${path}`;
  } catch {
    return null;
  }
}

export const CompanySection: React.FC<CompanySectionProps> = memo(({
  displayJob,
  companyId,
  companyIndustry,
  listingCompanyIndustry,
  companyAbout,
  companyContactDetails,
  companyRevenue,
  companyProfit,
  companyProfitMargin,
  companyMonthlyCounts,
  companyHeadCountMin,
  companyHeadCountMax,
  companyHeadcountRangeMin,
  companyHeadcountRangeMax,
  companyHQInFinland,
  companyVideo,
  linkedinUrl,
  facebookUrl,
  instagramUrl,
  youtubeUrl,
  tiktokUrl,
  registryMainBusinessLineDescriptionEn,
  registryMainBusinessLineDescriptionFi,
  registryMainBusinessLineDescriptionSv,
  registryCompanyFormDescription,
  registryWebsite,
  registryMainName,
  registryStreet,
  registryCity,
  registryPostCode,
  registryBuildingNumber,
  address,
  address_fi,
  address_sv,
  businessID,
  registryBusinessId,
  companyGeocoordinates,
  currentLocaleTrends,
  companyHiringTrendsByLocale,
  jobLocale,
  brandColour,
  useBrandColor = false,
  hasAnyCharts,
  chartsCollapsed,
  onToggleChartsCollapse,
  companyRef,
  isTyomarkkinatori = false,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const [companyInfoActiveTab, setCompanyInfoActiveTab] = useState<CompanyInfoTabId>('financial');
  const [showCompanyContactDetails, setShowCompanyContactDetails] = useState(false);
  const [showCompanyLocationMap, setShowCompanyLocationMap] = useState(false);
  const [companyNameCopied, setCompanyNameCopied] = useState(false);
  // SSR / first paint: expanded so charts and company body appear in HTML (crawlers, no-JS, #job-ssr shell).
  // After mount, apply saved preference on small screens only (desktop stays expanded).
  const [companyCardCollapsed, setCompanyCardCollapsed] = useState(false);
  const [hasHydratedCompanyPreference, setHasHydratedCompanyPreference] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsLargeScreen(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersCollapsed = readCompanyInfoCollapsedPreference();
    const isLg = window.matchMedia('(min-width: 1024px)').matches;
    setCompanyCardCollapsed(isLg ? false : prefersCollapsed);
    setHasHydratedCompanyPreference(true);
  }, []);

  useEffect(() => {
    if (!hasHydratedCompanyPreference) return;
    if (isLargeScreen) {
      setCompanyCardCollapsed(false);
    }
  }, [isLargeScreen, hasHydratedCompanyPreference]);

  useEffect(() => {
    if (!hasHydratedCompanyPreference) return;
    if (isLargeScreen) return;
    writeCompanyInfoCollapsedPreference(companyCardCollapsed);
  }, [companyCardCollapsed, hasHydratedCompanyPreference, isLargeScreen]);

  const isCompanyCollapsible = !isLargeScreen;
  const shouldShowCollapsedPreview = isCompanyCollapsible && companyCardCollapsed;
  const currentLocationDistribution = currentLocaleTrends?.locationDistribution ?? {};
  const hiringSnapshotSeoRows = buildHiringSnapshotSeoRows(currentLocaleTrends?.locationDistribution);
  const roleSnapshotSeoRows = buildHiringSnapshotSeoRows(currentLocaleTrends?.roleDistribution);
  const roleLevelSnapshotSeoRows = buildHiringSnapshotSeoRows(currentLocaleTrends?.roleLevelDistribution);
  const locationPreviewEntries = Object.entries(currentLocationDistribution)
    .filter(([, value]) => hasAnyValidNumber(value))
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 3);
  const previewMax = locationPreviewEntries.length > 0
    ? Math.max(...locationPreviewEntries.map(([, v]) => Number(v)))
    : 0;

  const effectiveBrandColour = (useBrandColor && brandColour) ? brandColour : DEFAULT_BRAND_GREEN;

  // Get first company URL from semicolon-separated list
  const firstCompanyUrl = getFirstCompanyUrl(displayJob?.companyURL);
  const companyUrlToUse = firstCompanyUrl || displayJob?.companyURL;

  const mainBusinessLine = resolveRegistryMainBusinessLineForPageLocale(
    {
      registryMainBusinessLineDescriptionEn,
      registryMainBusinessLineDescriptionFi,
      registryMainBusinessLineDescriptionSv,
    },
    jobLocale,
  );

  // Show main business line when present and not redundant with industry lines (or when industry is missing).
  const shouldShowMainBusinessLine = Boolean(
    mainBusinessLine?.trim() &&
      (!companyIndustry?.trim() ||
        mainBusinessLine.trim().toLowerCase() !== companyIndustry.trim().toLowerCase()) &&
      (!listingCompanyIndustry?.trim() ||
        mainBusinessLine.trim().toLowerCase() !== listingCompanyIndustry.trim().toLowerCase())
  );

  const companyAboutTrimmed = filterCompanyAboutForPageLocale(companyAbout, jobLocale);
  const mainBusinessLineInHeader = shouldShowMainBusinessLine;
  const aboutFallbackParagraphs: string[] = [];
  if (!companyAboutTrimmed) {
    const formDesc = registryCompanyFormDescription?.trim();
    if (formDesc) aboutFallbackParagraphs.push(formDesc);
    const mbl = mainBusinessLine?.trim();
    if (mbl && !mainBusinessLineInHeader) aboutFallbackParagraphs.push(mbl);
  }
  const showAboutSection =
    !isTyomarkkinatori && (companyAboutTrimmed.length > 0 || aboutFallbackParagraphs.length > 0);

  const registrySiteHref = registryWebsite?.trim() ? withHttpsUrl(registryWebsite.trim()) : '';
  const companySiteKey = firstCompanyUrl ? siteComparableKey(firstCompanyUrl) : null;
  const registrySiteKey = registryWebsite?.trim() ? siteComparableKey(registryWebsite.trim()) : null;
  const showRegistryWebsiteLink = Boolean(
    registrySiteHref && (!firstCompanyUrl || !registrySiteKey || companySiteKey !== registrySiteKey)
  );

  // Build address string
  let addressDisplay: string | null = null;
  if (address) {
    addressDisplay = address;
  } else {
    const languageAddress = (displayJob as any)[`address_${jobLocale}`];
    if (languageAddress) {
      addressDisplay = languageAddress;
    } else if (jobLocale === 'en' && address_fi) {
      addressDisplay = address_fi;
    } else if (address_fi) {
      addressDisplay = address_fi;
    } else if (address_sv) {
      addressDisplay = address_sv;
    } else {
      const addressParts = [
        registryStreet,
        registryBuildingNumber,
        registryPostCode,
        registryCity,
      ].filter(Boolean);
      addressDisplay = addressParts.length > 0 ? addressParts.join(' ') : null;
    }
  }

  // Extract YouTube video ID
  const youtubeVideoId = extractYouTubeVideoId(companyVideo);

  // Get business ID
  const businessId = registryBusinessId || businessID;

  // Set default tab based on available data
  useEffect(() => {
    if (!chartsCollapsed) {
      const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);
      const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);
      const hasLocations = !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
         (currentLocaleTrends.remotenessDistribution &&
          (currentLocaleTrends.remotenessDistribution.remote > 0 ||
           currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
           currentLocaleTrends.remotenessDistribution.onsite > 0))));
      const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));
      
      const currentTabHasData = 
        (companyInfoActiveTab === 'financial' && hasFinancial) ||
        (companyInfoActiveTab === 'hiring' && hasHiring) ||
        (companyInfoActiveTab === 'roles' && hasRoles) ||
        (companyInfoActiveTab === 'locations' && hasLocations) ||
        (companyInfoActiveTab === 'languages' && !!(currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)))) ||
        (companyInfoActiveTab === 'map' && !!companyGeocoordinates);
      
      if (!currentTabHasData) {
        if (hasFinancial) setCompanyInfoActiveTab('financial');
        else if (hasHiring) setCompanyInfoActiveTab('hiring');
        else if (hasRoles) setCompanyInfoActiveTab('roles');
        else if (hasLocations) setCompanyInfoActiveTab('locations');
        else if (currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val))) {
          setCompanyInfoActiveTab('languages');
        } else if (companyGeocoordinates) {
          setCompanyInfoActiveTab('map');
        }
      }
    }
  }, [chartsCollapsed, companyRevenue, companyProfit, companyProfitMargin, companyMonthlyCounts, currentLocaleTrends, companyGeocoordinates, companyInfoActiveTab]);

  type TabConfig = {
    id: CompanyInfoTabId;
    label: string;
    labelKey: string;
    hasData: boolean;
  };

  const availableTabs: TabConfig[] = [
    {
      id: 'financial',
      label: 'Financials',
      labelKey: 'jobDetailPage.companyTab.financials',
      hasData: hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin),
    },
    {
      id: 'hiring',
      label: 'Hiring Trends',
      labelKey: 'jobDetailPage.companyTab.hiringTrends',
      hasData: !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0),
    },
    {
      id: 'roles',
      label: 'Roles Hired',
      labelKey: 'jobDetailPage.companyTab.rolesHired',
      hasData: !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution)),
    },
    {
      id: 'locations',
      label: 'Locations Hired',
      labelKey: 'jobDetailPage.companyTab.locationsHired',
      hasData: !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
         (currentLocaleTrends.remotenessDistribution &&
          (currentLocaleTrends.remotenessDistribution.remote > 0 ||
           currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
           currentLocaleTrends.remotenessDistribution.onsite > 0)))),
    },
    {
      id: 'languages',
      label: 'Languages',
      labelKey: 'jobDetailPage.languageRequirements',
      hasData: !!(currentLocaleTrends?.languageRequirements &&
        Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
        Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val))),
    },
    {
      id: 'map',
      label: 'Map',
      labelKey: 'jobDetailPage.map',
      hasData: !!companyGeocoordinates,
    },
  ].filter(tab => tab.hasData);

  const renderTabButton = (tab: TabConfig) => {
    const handleClick = () => {
      setCompanyInfoActiveTab(tab.id);
      if (tab.id === 'map') {
        setShowCompanyLocationMap(true);
      }
    };
    const isActive = companyInfoActiveTab === tab.id;
    const useBrandColorForButton = useBrandColor && brandColour;
    
    return (
      <button
        key={tab.id}
        type="button"
        onClick={handleClick}
        className={`w-full sm:w-auto h-10 px-3.5 text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer rounded-lg border ${
          isActive
            ? useBrandColorForButton
              ? 'text-white shadow-sm border-transparent hover:brightness-95'
              : 'bg-jc-cta text-white border-jc-cta hover:brightness-95'
            : 'bg-jc-surface text-jc-ink-secondary border-jc-border hover:bg-jc-elevated hover:border-jc-accent/30'
        }`}
        style={isActive && useBrandColorForButton ? {
          backgroundColor: effectiveBrandColour,
          borderColor: effectiveBrandColour,
        } : undefined}
      >
        <Msg id={tab.labelKey} defaultMessage={tab.label} />
      </button>
    );
  };

  const brandTint = hexToRgba(effectiveBrandColour, 0.18);
  const brandBorder = hexToRgba(effectiveBrandColour, 0.35);
  const companyCardStyle = useBrandColor && brandColour
    ? {
        background: `linear-gradient(135deg, ${brandTint ?? 'rgba(59, 94, 69, 0.18)'} 0%, var(--jc-elevated) 100%)`,
        borderColor: brandBorder ?? 'rgba(59, 94, 69, 0.35)',
      }
    : undefined;

  return (
    <div ref={companyRef} data-section="company" className="scroll-mt-20 mt-10" id="company">
      <div
        className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-jc-accent-muted/50 to-jc-elevated border-2 border-jc-accent/20 dark:border-jc-border shadow-md dark:shadow-none transition-colors duration-200"
        style={companyCardStyle}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wide font-semibold text-jc-accent">
              <Msg
                id="jobDetailPage.section.company"
                defaultMessage="Company Information"
                description="Tab label for company information section showing company details and statistics"
              />
            </p>
            <button
              type="button"
              onClick={() => {
                if (!isCompanyCollapsible) return;
                setCompanyCardCollapsed((prev) => !prev);
              }}
              className={`text-jc-accent hover:brightness-90 transition-transform p-0.5 rounded-lg hover:bg-jc-surface/60 ${isCompanyCollapsible ? 'cursor-pointer' : 'hidden'}`}
              aria-expanded={!companyCardCollapsed}
              aria-controls="company-card-content"
              aria-label={companyCardCollapsed
                ? formatMessage({
                    id: 'jobDetailPage.expandCompanyData',
                    defaultMessage: 'Expand company information to view trends, distributions, and map',
                  })
                : formatMessage({
                    id: 'jobDetailPage.hideCharts',
                    defaultMessage: 'Hide Charts',
                  })}
            >
              {companyCardCollapsed ? (
                <CaretRight className="h-4 w-4" />
              ) : (
                <CaretDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Top Section: Logo, Company Info, Social Media - Responsive Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 sm:gap-6 ${
            shouldShowCollapsedPreview
              ? 'mb-3'
              : 'mb-5 pb-5 border-b border-jc-border/60'
          }`}
        >
          {/* Logo Section */}
          <div className="flex justify-center sm:justify-start">
            {companyUrlToUse ? (
              <a
                href={companyUrlToUse}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackCompanyLink({
                    companyId: companyId || '',
                    locale: intl.locale,
                  });
                }}
                className="hover:opacity-80 transition-opacity"
              >
                <CompanyLogo
                  companyName={companyId || ''}
                  companyURL={companyUrlToUse}
                  disableLink
                  size="large"
                  initialsSourceName={displayJob?.companyName ?? companyId ?? ''}
                  registryCompanyFormDescription={registryCompanyFormDescription}
                  isTyomarkkinatori={displayJob?.isTyomarkkinatori}
                />
              </a>
            ) : (
              <CompanyLogo
                companyName={companyId || ''}
                size="large"
                initialsSourceName={displayJob?.companyName ?? companyId ?? ''}
                registryCompanyFormDescription={registryCompanyFormDescription}
                isTyomarkkinatori={displayJob?.isTyomarkkinatori}
              />
            )}
          </div>
        
          {/* Company Info and Social Media */}
          <div className="flex flex-col gap-3">
            {/* Company Name and Details */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                {companyUrlToUse ? (
                  <a
                    href={companyUrlToUse}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackCompanyLink({
                        companyId: companyId || '',
                        locale: intl.locale,
                      });
                    }}
                    className="flex-1 hover:opacity-80 transition-opacity"
                  >
                    <h4 className="font-bold text-xl sm:text-2xl text-jc-ink hover:text-jc-accent transition-colors">
                      <CompanyName name={companyId ?? ''} />
                    </h4>
                  </a>
                ) : (
                  <h4 className="font-bold text-xl sm:text-2xl text-jc-ink flex-1">
                    <CompanyName name={companyId ?? ''} />
                  </h4>
                )}
                {companyId && (
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(companyId);
                        setCompanyNameCopied(true);
                        setTimeout(() => setCompanyNameCopied(false), 2000);
                      } catch (err) {
                        console.error('Failed to copy company name:', err);
                      }
                    }}
                    className="p-1.5 text-jc-muted hover:text-jc-ink hover:bg-jc-elevated rounded-lg transition-colors"
                    title={formatMessage(
                      { id: 'job.copyCompanyName', defaultMessage: 'Copy company name', description: 'Button tooltip to copy company name to clipboard' },
                      { companyName: companyId }
                    )}
                  >
                    {companyNameCopied ? (
                      <Check className="w-4 h-4 text-jc-accent" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              {/* Company Details Grid */}
              <div className="space-y-1.5 text-sm">
                {companyIndustry && (
                  <div className="text-jc-ink-secondary font-medium">
                    {fixMojibake(companyIndustry)}
                  </div>
                )}
                {!companyIndustry && listingCompanyIndustry && (
                  <div className="text-jc-ink-secondary font-medium">
                    {fixMojibake(listingCompanyIndustry)}
                  </div>
                )}
                {companyIndustry &&
                  listingCompanyIndustry &&
                  listingCompanyIndustry.trim().toLowerCase() !== companyIndustry.trim().toLowerCase() && (
                    <div className="text-sm text-jc-muted">
                      <span className="font-medium text-jc-ink-secondary">
                        <Msg
                          id="jobDetailPage.listingCompanyIndustryLabel"
                          defaultMessage="Listing"
                          description="Label when listing-inferred industry differs from registered company industry"
                        />
                      </span>
                      : {fixMojibake(listingCompanyIndustry)}
                    </div>
                  )}
                {shouldShowMainBusinessLine && (
                  <div className="text-sm text-jc-muted">
                    {fixMojibake(mainBusinessLine)}
                  </div>
                )}
                {addressDisplay && (
                  <div className="text-sm text-jc-muted flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 flex-shrink-0 text-jc-accent" />
                    <span>{fixMojibake(addressDisplay)}</span>
                  </div>
                )}
                {businessId && (
                  <div className="text-xs text-jc-muted font-mono">
                    <Msg 
                      id="jobDetailPage.businessId" 
                      defaultMessage="Business ID" 
                      description="Label for company business registration ID" 
                    />: {businessId}
                  </div>
                )}
                {registryMainName &&
                  companyId &&
                  registryMainName.trim().toLowerCase() !== companyId.trim().toLowerCase() && (
                    <div className="text-xs text-jc-muted">
                      <span className="font-medium text-jc-ink-secondary">
                        <Msg
                          id="jobDetailPage.officialRegisterName"
                          defaultMessage="Registered name"
                          description="Label for official company name from Finnish trade register"
                        />
                      </span>
                      : {fixMojibake(registryMainName)}
                    </div>
                  )}
                {showRegistryWebsiteLink && (
                  <div className="text-sm">
                    <a
                      href={registrySiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-jc-accent hover:text-jc-cta-hover underline transition-colors"
                      onClick={() =>
                        trackCompanyLink({
                          companyId: companyId || '',
                          locale: intl.locale,
                        })
                      }
                    >
                      <Msg
                        id="jobDetailPage.tradeRegisterWebsite"
                        defaultMessage="Trade register website"
                        description="Link to company website URL as recorded in Finnish trade register"
                      />
                    </a>
                  </div>
                )}
                {(companyHQInFinland || 
                  isValidNumber(companyHeadCountMin) || 
                  isValidNumber(companyHeadCountMax) ||
                  isValidNumber(companyHeadcountRangeMin) || 
                  isValidNumber(companyHeadcountRangeMax)) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                    {companyHQInFinland && (
                      <div className="text-sm text-jc-ink-secondary">
                        <strong className="text-jc-ink">HQ:</strong> {fixMojibake(companyHQInFinland)}
                      </div>
                    )}
                    {(() => {
                      const min = getValidNumberOrNull(companyHeadCountMin ?? companyHeadcountRangeMin);
                      const max = getValidNumberOrNull(companyHeadCountMax ?? companyHeadcountRangeMax);
                      const hasValidHeadcount = (min !== null && min > 0) || (max !== null && max > 0);
                      if (!hasValidHeadcount) return null;
                      
                      return (
                        <div className="text-sm text-jc-ink-secondary">
                          <strong className="text-jc-ink">
                            <Msg 
                              id="jobDetailPage.headcount" 
                              defaultMessage="Headcount" 
                              description="Label for company employee count/headcount field" 
                            />:
                          </strong>{' '}
                          {min !== null && max !== null && min === max
                            ? min.toLocaleString()
                            : `${min !== null ? min.toLocaleString() : '?'} - ${max !== null ? max.toLocaleString() : '?'}`}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          
            {/* Social Media and YouTube */}
            <div className="flex items-start sm:items-center gap-2.5 flex-wrap">
              <SocialMediaButtons
                linkedinUrl={linkedinUrl}
                facebookUrl={facebookUrl}
                instagramUrl={instagramUrl}
                youtubeUrl={youtubeUrl}
                tiktokUrl={tiktokUrl}
                companyId={companyId}
                locale={intl.locale}
              />
              {youtubeVideoId && (
                <a
                  href={`https://www.youtube.com/watch?v=${youtubeVideoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-jc-cta text-white rounded-xl text-sm font-medium hover:brightness-95 active:brightness-90 transition-[filter]"
                  onClick={() => {
                    trackCompanyLink({
                      companyId: companyId || '',
                      locale: intl.locale,
                    });
                  }}
                >
                  <svg 
                    className="w-5 h-5" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <Msg 
                    id="jobDetailPage.watchCompanyVideo" 
                    defaultMessage="Watch Company Video" 
                    description="Button label to watch company introduction or promotional video" 
                  />
                </a>
              )}
            </div>
          </div>
        </div>

        {shouldShowCollapsedPreview && (
          <div
            className="mb-5 -mx-2 px-2 py-1 rounded-lg cursor-pointer transition-colors hover:bg-jc-elevated/50"
            role="button"
            tabIndex={0}
            onClick={() => setCompanyCardCollapsed(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCompanyCardCollapsed(false);
              }
            }}
            aria-label={formatMessage({
              id: 'jobDetailPage.expandCompanyData',
              defaultMessage: 'Expand company information to view trends, distributions, and map',
            })}
          >
            <p className="text-xs text-jc-ink-secondary mb-2">
              <Msg
                id="jobDetailPage.companyCollapsedBody"
                defaultMessage="Expand to view trend charts, location distribution, language demand, and map details."
                description="Body text shown in collapsed company information card encouraging expansion to view detailed charts and analytics"
              />
            </p>
            <div className="space-y-1.5">
              {locationPreviewEntries.map(([label, value]) => {
                const numeric = Number(value);
                const widthPercent = previewMax > 0 ? Math.max(8, Math.round((numeric / previewMax) * 100)) : 8;
                return (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-[11px] text-jc-muted w-28 truncate">{label}</span>
                    <div className="h-1.5 flex-1 rounded-full bg-jc-border/60 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-jc-accent/80"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {locationPreviewEntries.length === 0 && (
                <div className="h-1.5 rounded-full bg-jc-border/60 overflow-hidden">
                  <div className="h-full w-1/3 rounded-full bg-jc-accent/70" />
                </div>
              )}
            </div>
          </div>
        )}

        {hiringSnapshotSeoRows.length > 0 ? (
          <div className="sr-only">
            <table>
              <caption>
                {formatMessage(
                  {
                    id: 'jobDetailPage.hiringSnapshotSeo.caption',
                    defaultMessage: 'Current open roles at {company} on JobCrawls',
                    description: 'Hidden hiring snapshot table caption for SEO',
                  },
                  { company: companyId ?? 'Company' },
                )}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    <Msg
                      id="jobDetailPage.hiringSnapshotSeo.location"
                      defaultMessage="Location"
                      description="Hidden hiring snapshot table header"
                    />
                  </th>
                  <th scope="col">
                    <Msg
                      id="jobDetailPage.hiringSnapshotSeo.activeListings"
                      defaultMessage="Active listings"
                      description="Hidden hiring snapshot table header"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {hiringSnapshotSeoRows.map((row) => (
                  <tr key={row.location}>
                    <td>{row.location}</td>
                    <td>{row.activeListings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {roleSnapshotSeoRows.length > 0 ? (
              <table>
                <caption>
                  {formatMessage(
                    {
                      id: 'jobDetailPage.roleSnapshotSeo.caption',
                      defaultMessage: 'Current role mix at {company} on JobCrawls',
                      description: 'Hidden role snapshot table caption for SEO',
                    },
                    { company: companyId ?? 'Company' },
                  )}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">
                      <Msg
                        id="jobDetailPage.roleSnapshotSeo.role"
                        defaultMessage="Role type"
                        description="Hidden role snapshot table header"
                      />
                    </th>
                    <th scope="col">
                      <Msg
                        id="jobDetailPage.roleSnapshotSeo.activeListings"
                        defaultMessage="Active listings"
                        description="Hidden role snapshot table header"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleSnapshotSeoRows.map((row) => (
                    <tr key={row.location}>
                      <td>{row.location}</td>
                      <td>{row.activeListings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {roleLevelSnapshotSeoRows.length > 0 ? (
              <table>
                <caption>
                  {formatMessage(
                    {
                      id: 'jobDetailPage.roleLevelSnapshotSeo.caption',
                      defaultMessage: 'Current role-level mix at {company} on JobCrawls',
                      description: 'Hidden role-level snapshot table caption for SEO',
                    },
                    { company: companyId ?? 'Company' },
                  )}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">
                      <Msg
                        id="jobDetailPage.roleLevelSnapshotSeo.level"
                        defaultMessage="Role level"
                        description="Hidden role-level snapshot table header"
                      />
                    </th>
                    <th scope="col">
                      <Msg
                        id="jobDetailPage.roleLevelSnapshotSeo.activeListings"
                        defaultMessage="Active listings"
                        description="Hidden role-level snapshot table header"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleLevelSnapshotSeoRows.map((row) => (
                    <tr key={row.location}>
                      <td>{row.location}</td>
                      <td>{row.activeListings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : null}

        <div
          id="company-card-content"
          aria-hidden={companyCardCollapsed}
          className={companyCardCollapsed ? 'max-h-0 overflow-hidden opacity-0 pointer-events-none' : 'opacity-100'}
        >
            {/* Company dataset insight */}
            {!isTyomarkkinatori && (
              <div className="mb-5">
                <CompanyInsightCard
                  companyId={companyId}
                  companyHiringTrendsByLocale={companyHiringTrendsByLocale}
                  companyMonthlyCounts={companyMonthlyCounts}
                  locale={jobLocale}
                />
              </div>
            )}

            {/* Tyomarkkinatori explanation */}
            {isTyomarkkinatori && (
              <div className="mt-2 pt-5 border-t border-jc-border/60">
                <p className="text-sm text-jc-ink-secondary leading-relaxed [&_strong]:text-jc-ink">
                  <Msg
                    id="jobDetailPage.tyomarkkinatori.about"
                    defaultMessage="<strong>Työmarkkinatori</strong> is Finland's official public employment service portal, operated by the Ministry of Economic Affairs and Employment (TEM). It lists job openings from across the Finnish public sector, municipalities, and employers who use the government's recruitment infrastructure."
                    description="Explanation of what Tyomarkkinatori is, shown on job detail pages sourced from that portal"
                    values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                  />
                </p>
                <p className="text-sm text-jc-muted leading-relaxed mt-2">
                  <Msg
                    id="jobDetailPage.tyomarkkinatori.whyWeIndex"
                    defaultMessage="We include Työmarkkinatori listings so that JobCrawls covers the full breadth of the Finnish job market — including roles that only appear through official government channels and would otherwise be missed."
                    description="Explanation of why JobCrawls indexes Tyomarkkinatori listings, shown on job detail pages from that source"
                  />
                </p>
                <a
                  href="https://tyomarkkinatori.fi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-sm text-jc-accent hover:text-jc-cta-hover underline transition-colors"
                >
                  tyomarkkinatori.fi
                </a>
              </div>
            )}

        {/* About: crawled company copy and/or trade-register fields enriched onto the job */}
        {showAboutSection && (
          <div className="mb-5 pt-5 border-t border-jc-border/60">
            <h5 className="text-sm font-semibold text-jc-ink mb-2">
              <Msg 
                id="jobDetailPage.aboutCompany" 
                defaultMessage="About" 
                description="Section heading for company description/about text" 
              />
            </h5>
            {!companyAboutTrimmed && aboutFallbackParagraphs.length > 0 && (
              <p className="text-xs text-jc-muted mb-2">
                <Msg
                  id="jobDetailPage.companyInfoFromTradeRegister"
                  defaultMessage="Includes details from the Finnish Trade Register."
                  description="Note when About section is filled from PRH register fields rather than a separate company profile"
                />
              </p>
            )}
            {companyAboutTrimmed ? (
              <p className="text-sm text-jc-ink-secondary leading-relaxed">
                {fixMojibake(companyAboutTrimmed)}
              </p>
            ) : (
              aboutFallbackParagraphs.map((text, i) => (
                <p
                  key={`about-${i}`}
                  className="text-sm text-jc-ink-secondary leading-relaxed [&:not(:last-child)]:mb-3"
                >
                  {fixMojibake(text)}
                </p>
              ))
            )}
          </div>
        )}

        {/* Contact Details */}
        {!isTyomarkkinatori && companyContactDetails && (
          <div className="mb-5 pt-5 border-t border-jc-border/60">
            {!showCompanyContactDetails ? (
              <button
                type="button"
                className="text-sm text-jc-accent hover:text-jc-cta-hover underline px-2 py-1 border border-jc-accent/25 rounded-lg hover:bg-jc-accent-muted/40 cursor-pointer transition-colors"
                onClick={() => setShowCompanyContactDetails(true)}
              >
                <Msg 
                  id="jobDetailPage.showCompanyContact" 
                  defaultMessage="Show Company Contact" 
                  description="Button label to reveal company contact information" 
                />
              </button>
            ) : (
              <div className="text-sm text-jc-ink-secondary">
                <strong className="text-jc-ink">Contact:</strong> {companyContactDetails}
              </div>
            )}
          </div>
        )}

        {/* Charts and Data Grid */}
        {!isTyomarkkinatori && <div className="mt-6">
          {/* Collapse/Expand Button */}
          {hasAnyCharts && (
            <div className="mb-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onToggleChartsCollapse}
                className="inline-flex items-center gap-2 rounded-lg border border-jc-border/80 bg-jc-page px-3 py-1.5 text-sm font-medium text-jc-ink-secondary hover:text-jc-ink hover:bg-jc-elevated transition-colors cursor-pointer"
              >
                {chartsCollapsed ? (
                  <>
                    <CaretRight className="w-4 h-4" />
                    <Msg
                      id="jobDetailPage.showCharts"
                      defaultMessage="Show Charts"
                      description="Button label to expand and show company statistics charts"
                    />
                  </>
                ) : (
                  <>
                    <CaretDown className="w-4 h-4" />
                    <Msg
                      id="jobDetailPage.hideCharts"
                      defaultMessage="Hide Charts"
                      description="Button label to collapse and hide company statistics charts"
                    />
                  </>
                )}
              </button>
            </div>
          )}

          {!chartsCollapsed && (
            <>
              {/* Chart tabs */}
              {availableTabs.length > 0 && (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
                    {availableTabs.map(renderTabButton)}
                  </div>
                </div>
              )}

              {/* Chart content */}
              <div className="min-h-[320px] w-full">
                {companyInfoActiveTab === 'financial' && hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin) && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full">
                    <MoneyGraph
                      revenue={companyRevenue}
                      profit={companyProfit}
                      profitMargin={companyProfitMargin}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'hiring' && companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0 && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full">
                    <MonthlyHiringChart 
                      monthlyCounts={companyMonthlyCounts}
                      headcountMin={getValidNumberOrNull(companyHeadCountMin ?? companyHeadcountRangeMin) ?? undefined}
                      headcountMax={getValidNumberOrNull(companyHeadCountMax ?? companyHeadcountRangeMax) ?? undefined}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'roles' && currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution) && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full">
                    <RoleRoleLevelChart
                      roleDistribution={currentLocaleTrends.roleDistribution}
                      roleLevelDistribution={currentLocaleTrends.roleLevelDistribution}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'locations' && currentLocaleTrends &&
                  ((currentLocaleTrends.locationDistribution &&
                    Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
                    Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
                   (currentLocaleTrends.remotenessDistribution &&
                    (currentLocaleTrends.remotenessDistribution.remote > 0 ||
                     currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
                     currentLocaleTrends.remotenessDistribution.onsite > 0))) && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full flex items-center justify-center">
                    <DonutSunburstChart
                      outerData={currentLocaleTrends.locationDistribution || {}}
                      innerData={{
                        Remote: currentLocaleTrends.remotenessDistribution?.remote || 0,
                        Hybrid: currentLocaleTrends.remotenessDistribution?.hybrid || 0,
                        Onsite: currentLocaleTrends.remotenessDistribution?.onsite || 0,
                      }}
                      outerTitle={formatMessage({ id: "jobDetailPage.locationDistribution", defaultMessage: "Location Distribution", description: "Chart title for location distribution showing where company posts jobs" })}
                      innerTitle={formatMessage({ id: "jobDetailPage.remotenessDistribution.short", defaultMessage: "Remoteness", description: "Short label for remoteness distribution chart" })}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                      className="w-full max-w-[300px] mx-auto"
                      maxInnerItems={3}
                      minOuterPercentage={3}
                      outerCaption={
                        <div className="text-sm text-center text-jc-muted">
                          <Msg 
                            id="jobDetailPage.locationDistribution" 
                            defaultMessage="Location Distribution" 
                            description="Chart title for location distribution showing where company posts jobs" 
                          />
                        </div>
                      }
                      innerCaption={
                        <div className="text-sm text-center text-jc-muted mt-2">
                          <Msg 
                            id="jobDetailPage.byRemoteness" 
                            defaultMessage="By Remoteness" 
                            description="Chart caption label indicating data is grouped by work model (remote, hybrid, onsite)" 
                          />
                        </div>
                      }
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'languages' && currentLocaleTrends?.languageRequirements &&
                  Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
                  Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)) && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full">
                    <StarburstChart
                      data={currentLocaleTrends.languageRequirements}
                      title={formatMessage({ id: "jobDetailPage.languageRequirements", defaultMessage: "Language Requirements", description: "Chart title for language requirements distribution showing which languages companies typically require" })}
                      titleKey="languageRequirements"
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'map' && companyGeocoordinates && (
                  <div className="bg-jc-page/90 border border-jc-border/70 p-4 rounded-xl w-full">
                    {showCompanyLocationMap ? (
                      <Suspense fallback={<div className="w-full h-64 bg-jc-page/50 rounded-xl animate-pulse" aria-hidden="true" />}>
                        <CompanyLocationMap
                          locations={[{
                            lat: companyGeocoordinates.lat,
                            lng: companyGeocoordinates.lng,
                            address: addressDisplay,
                            isPrimary: true,
                          }]}
                          companyName={companyId}
                          companyId={companyId}
                        />
                      </Suspense>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowCompanyLocationMap(true)}
                        className="w-full px-4 py-3 bg-jc-cta text-white rounded-xl font-medium cursor-pointer hover:brightness-95 active:brightness-90 transition-[filter]"
                      >
                        <Msg 
                          id="jobDetailPage.seeLocation" 
                          defaultMessage="See Location" 
                          description="Button label to view company location on a map" 
                        />
                      </button>
                    )}
                  </div>
                )}

                {/* Data range disclaimer */}
                {companyInfoActiveTab !== 'map' && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-jc-muted">
                      <Msg 
                        id="jobDetailPage.chartsDataRange" 
                        defaultMessage="Data shown is based on historical job postings from our database."
                        description="Disclaimer text explaining that chart data is based on historical job posting data" 
                      />
                    </p>
                  </div>
                )}
              </div>

            </>
          )}
        </div>}
          </div>
      </div>
    </div>
  );
});

CompanySection.displayName = 'CompanySection';
